import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Modal from '../Modal';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Button from '../Button';
import Link from '../Link';

import SafetyMeasureCategoryIcon from '../SafetyMeasureCategoryIcon';
import SafetyMeasureList from '../SafetyMeasureList';

import './SafetyMeasures.scss';

const SafetyMeasures = ({
  categories,
  onViewAllClick,
  url,
  venueName,
  isMobile,
  referrer,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [totalSafetyMeasures, setTotalSafetyMeasures] = useState(0);

  const calculateTotalSafetyMeasures = (categoriesArg) => {
    const total = categoriesArg.reduce(
      (sum, category) => sum + category.items.filter((item) => item.active).length,
      0
    );
    setTotalSafetyMeasures(total);
  };

  useEffect(() => {
    if (categories.length > 0) {
      calculateTotalSafetyMeasures(categories);
    }
  }, [categories]);

  const toggleModal = () => {
    setModalIsOpen((prevState) => !prevState);
  };

  const handleViewAllClick = () => {
    if (onViewAllClick && typeof onViewAllClick === 'function') {
      onViewAllClick();
    } else {
      toggleModal();
    }
  };

  const getModalStyles = () => {
    const overlay = {
      backgroundColor: 'rgba(0, 0, 0, 0.35)',
      zIndex: '10',
      display: 'flex',
      alignItems: 'center',
    };

    const content = {
      position: 'relative',
      padding: '0',
      left: '0',
      top: '0',
      right: '0',
      bottom: '0',
      width: isMobile ? '100%' : '700px',
      height: isMobile ? '100%' : 'auto',
      maxHeight: isMobile ? '100%' : '85%',
      margin: '0 auto',
      border: 'none',
      borderRadius: isMobile ? '0' : '15px',
      boxShadow: '0 1px 12px 0 rgba(0, 0, 0, 0.08)',
      backgroundColor: '#fafafa',
    };

    return {
      overlay,
      content,
    };
  };

  const renderSeeAll = () => {
    if (totalSafetyMeasures === 0) {
      return null;
    }

    if (totalSafetyMeasures === 1) {
      return (
        <TranslationProvider translationKey="view_all_safety_measures">
          <Link className="SafetyMeasures__view-all" onClick={handleViewAllClick}>
            View all safety measures
          </Link>
        </TranslationProvider>
      );
    }

    return (
      <TranslationProvider
        translationKey="view_num_safety_measures"
        translationValues={{ totalSafetyMeasures: totalSafetyMeasures.toString() }}
      >
        <Button className="SafetyMeasures__view-all" variant="link" onClick={handleViewAllClick}>
          {`View all ${totalSafetyMeasures} safety measures`}
        </Button>
      </TranslationProvider>
    );
  };

  const renderModal = () => (
    <Modal
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      isOpen={modalIsOpen}
      style={getModalStyles()}
      onRequestClose={toggleModal}
    >
      <div className="AnnouncementModal__button-container">
        <Button
          className="AnnouncementModal__icon-close"
          type="button"
          onClick={toggleModal}
          variant="close"
          aria-label={getTranslation({ translationKey: 'close', fallback: 'Close' })}
        />
      </div>
      <SafetyMeasureList
        categories={categories}
        url={url}
        venueName={venueName}
        referrer={referrer}
      />
    </Modal>
  );

  if (totalSafetyMeasures === 0) {
    return null;
  }

  return (
    <div className="SafetyMeasures" data-testid="Safety-Measures">
      <TranslationProvider translationKey="safety_measures">
        <h2 className="SafetyMeasures__title">Safety Measures</h2>
      </TranslationProvider>
      <TranslationProvider translationKey="health_safety_description">
        <div className="SafetyMeasures__description">
          This restaurant has committed to taking certain safety measures to
          provide a safe, comfortable dining experience.
        </div>
      </TranslationProvider>
      <div className="SafetyMeasures__categories-container">
        {categories.map((category) => {
          if (category.items.length === 0) {
            return null;
          }
          return (
            <SafetyMeasureCategoryIcon
              category={category}
              key={category.id}
            />
          );
        })}
      </div>
      {renderSeeAll()}
      {modalIsOpen && renderModal()}
      <hr />
    </div>
  );
};

SafetyMeasures.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  onViewAllClick: PropTypes.func,
  url: PropTypes.string,
  venueName: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  referrer: PropTypes.oneOf(['venue page', 'event page', 'widget']),
};

SafetyMeasures.defaultProps = {
  categories: [],
  onViewAllClick: undefined,
  url: '',
  referrer: 'venue page',
  isMobile: false,
};

SafetyMeasures.displayName = 'SafetyMeasures';

export default SafetyMeasures;
